import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHero, EmptyState, Reveal, PhotoSlot, GALLERY_CATEGORIES } from "@/components/shared";

export default function Galerie() {
  const [photos, setPhotos] = useState(null);
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState("toutes");

  useEffect(() => {
    // La galerie principale est dédiée au restaurant : les autres activités
    // (Comptoir, Services) ont chacune leur galerie dans leur propre page.
    api
      .get("/gallery")
      .then((r) => setPhotos(r.data.filter((p) => !p.scope || p.scope === "restaurant")))
      .catch(() => setPhotos([]));
    api
      .get("/media/videos")
      .then((r) => setVideos(r.data))
      .catch(() => {});
  }, []);

  const filtered = photos?.filter((p) => active === "toutes" || p.category === active) || [];
  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <div data-testid="galerie-page">
      <PageHero overline="Galerie" title="Le Palais Maasaï en images" testId="galerie-hero" />

      <section className="px-6 pb-28 md:px-12 md:pb-40">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <div className="mb-14 flex flex-wrap gap-2" data-testid="gallery-filters">
              {[{ id: "toutes", label: "Toutes" }, ...GALLERY_CATEGORIES].map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActive(cat.id)}
                  data-testid={`gallery-filter-${cat.id}`}
                  className={`rounded-md px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.15em] transition-colors duration-300 ${
                    active === cat.id ? "bg-copper text-ivory" : "text-ivory/50 hover:text-gold"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </Reveal>

          {photos === null ? null : filtered.length === 0 ? (
            <EmptyState text="Les photos arrivent bientôt." />
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="gallery-grid">
              {filtered.map((photo, i) => (
                <Reveal key={photo.id} delay={(i % 3) * 0.08}>
                  <figure data-testid={`gallery-item-${photo.id}`}>
                    <PhotoSlot src={photo.photo} alt={photo.caption} ratio="aspect-square" />
                    {photo.caption && (
                      <figcaption className="mt-3 text-xs uppercase tracking-[0.2em] text-ivory/40">
                        {photo.caption}
                      </figcaption>
                    )}
                  </figure>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {videos.length > 0 && (
        <section className="border-t border-white/5 px-6 py-20 md:px-12" data-testid="gallery-videos-section">
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <p className="overline mb-6">Vidéos</p>
              <h2 className="max-w-2xl text-2xl font-bold tracking-tight text-ivory sm:text-3xl">
                Le Palais Maasaï en mouvement
              </h2>
            </Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2" data-testid="gallery-videos-grid">
              {videos.map((video, i) => (
                <Reveal key={video.id} delay={(i % 2) * 0.08}>
                  <figure data-testid={`gallery-video-${video.id}`}>
                    <video
                      src={`${backend}/api/media/${video.id}/file`}
                      controls
                      preload="metadata"
                      className="aspect-video w-full rounded-md border border-white/10 bg-black object-cover"
                    />
                    <figcaption className="mt-3 text-xs uppercase tracking-[0.2em] text-ivory/40">
                      {video.filename}
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
